import { ReactNode } from 'react';
import { useTranslation } from '../../../../lib/i18n';
import { useProductName } from '../../../../lib/use-brand';
import { connectorsTargetElementId, type ChannelSlug } from '../../../../lib/connectors-route';
import { AI_CODERS_ITEM_TITLE_KEYS, filterChannelSections } from '../channel-search';
import { WhatsappMobile } from './WhatsappMobile';
import { AiCodersMobile } from './AiCodersMobile';
import { GeminiMobile } from './GeminiMobile';
import { WearablesMobile } from './WearablesMobile';
import { MeetingsChannelSection } from '../MeetingsChannelSection';
import { useMeetingsAgentEnabled } from '../../../../lib/meetings-agent-flag';
import styles from './mobile.module.css';

/**
 * The Channels half, mobile (Figma 13619-121017): the channel sections lead straight off the
 * Tools/Channels tab bar — WhatsApp, AI coder, Gemini Enterprise, Wearables — each a 16/20 title over a
 * 14/18 secondary blurb, then the group's own body. No "Channels" heading, Add, or divider: the tab
 * already names the view, and a channel isn't user-added (that lives on the Tools tab's MCP connect).
 *
 * ⚠️ Pre-existing gap, not introduced here: this list has no `symphony-apps` (Web & Apps) group either,
 * so it does not yet match `CHANNEL_SLUGS` in full — only Wearables was added to close the gap that
 * change was responsible for, and Meetings the gap this one is. `heroChannelNodes` (which feeds the
 * unreferenced `ConnectorsHero`, not the genome diagram actually rendered here) is unaffected either
 * way: that invariant is about a DIAGRAM, not this list.
 *
 * The connect logic is shared verbatim with desktop — the MCP endpoint, the A2A Agent Card and the
 * act-as-me token mint, the WhatsApp group hooks/modals, and `AgentConnectionsList` — so nothing about
 * how a grant is minted, listed or revoked changes on mobile. Only the card presentation does. Section
 * ids stay the shared `connectorsTargetElementId('channels', slug)`, so a deep link to a group (or a
 * live WhatsApp push) scrolls to the same anchor it does on desktop.
 */
export function ChannelsMobile({ target, query = '' }: { target?: string; query?: string }) {
  const { t } = useTranslation();
  const productName = useProductName();
  const mention = `@${productName.toLowerCase()}`;

  /**
   * Meetings is behind `symphonyMeetingsAgent`, and the gate has to be applied HERE as well as in
   * `ChannelsBody`: this list is written by hand rather than derived from `CHANNEL_SLUGS`, and the page
   * forks on viewport width, so a gate on the desktop list alone would leave every phone browser
   * outside the release looking at the section. Dropped from the list rather than skipped at render,
   * for the same reason desktop does it — `filterChannelSections` below must not count a section that
   * is not drawn.
   *
   * The card itself is shared verbatim with desktop, like every other body here: only the presentation
   * around a channel forks on mobile, never the logic inside it.
   */
  const meetingsEnabled = useMeetingsAgentEnabled();

  /**
   * A body is either drawn under the shared head, or — Meetings — handed the head's two parts so it can
   * put its Connected badge and menu on the title's own line.
   */
  const groups: { slug: ChannelSlug; body: ReactNode | ((title: ReactNode, about: ReactNode) => ReactNode) }[] = [
    { slug: 'whatsapp', body: <WhatsappMobile /> },
    ...(meetingsEnabled
      ? [
          {
            slug: 'meetings' as ChannelSlug,
            body: (title: ReactNode, about: ReactNode) => <MeetingsChannelSection title={title} about={about} headClassName={styles.channelHead} bodyInCard defaultExpanded={target === 'meetings'} />,
          },
        ]
      : []),
    { slug: 'ai-coders', body: <AiCodersMobile query={query} /> },
    { slug: 'gemini-enterprise', body: <GeminiMobile /> },
    { slug: 'wearables', body: <WearablesMobile /> },
  ];

  /**
   * The text a section is matched on: its own display title, except AI coders, which matches on its
   * ITEM titles so a coder name surfaces the section (the card is on the page) while the generic
   * "AI coders" title does not. Mirrors both native platforms.
   */
  const sectionSearchText = (slug: ChannelSlug): string =>
    slug === 'ai-coders'
      ? AI_CODERS_ITEM_TITLE_KEYS.map((key) => t(key)).join(' ')
      : t(`connectors.channel.${slug}`);

  const visible = filterChannelSections(groups, query, ({ slug }) => sectionSearchText(slug));

  if (query.trim() && visible.length === 0) {
    return (
      <div className={styles.noResults}>
        <h3 className={styles.sectionTitle}>{t('search.noResults')}</h3>
        <p className={styles.channelAbout}>{t('search.noResults.sub')}</p>
      </div>
    );
  }

  return (
    <div className={styles.channelGroups}>
      {visible.map(({ slug, body }) => {
        const title = <h3 className={styles.sectionTitle}>{t(`connectors.channel.${slug}`)}</h3>;
        // WhatsApp carries the fuller mobile blurb from its Figma frame; the other channels keep the
        // shared `connectors.about.*`. Extra params are ignored by keys without them.
        const about = (
          <p className={styles.channelAbout}>
            {t(slug === 'whatsapp' ? 'connectors.about.whatsapp.mobile' : `connectors.about.${slug}`, { productName, mention })}
          </p>
        );
        return (
          <section
            key={slug}
            id={connectorsTargetElementId('channels', slug)}
            className={styles.channelGroup}
            data-targeted={target === slug ? '' : undefined}
          >
            {typeof body === 'function' ? (
              body(title, about)
            ) : (
              <>
                <div className={styles.channelHead}>
                  {title}
                  {about}
                </div>
                {body}
              </>
            )}
          </section>
        );
      })}
    </div>
  );
}
